import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../../core/SceneObject';
import { Layout, Properties, Text } from '../../exports/behaviors';
import { boxFromMatrix, seenFrom, type Box, type V3 } from '../quality/geometry';
import { drawnBox } from '../quality/measure';

/** Content keeps this share of a card's smaller side clear of its edges; text above it keeps a line clear, at least that. */
const MARGIN = 0.05;

const mm = (v: number): string => `${Math.round(v * 1000)} mm`;

/** A card's face as a box: its Layout size at its origin, in its own axes; null before it has one. */
function faceOf(card: SceneObject): Box | null {
    const layout = card.getBehavior(Layout), data = layout?._getResolvedData();
    const size = layout?.getSolvedSize() ?? (data ? { x: data.width ?? NaN, y: data.height ?? NaN, z: data.depth ?? 0 } : null);
    if (!size || !(size.x > 0) || !(size.y > 0)) return null;
    return boxFromMatrix(card.transform.computeWorldMatrix(true).m, [0, 0, 0], [size.x / 2, size.y / 2, (size.z ?? 0) / 2]);
}

/** Move `object` by a world vector, written as a change of its position in its parent. */
function shift(object: SceneObject, world: V3): void {
    const parent = object.transform.parent;
    const local = parent ? Vector3.TransformNormal(new Vector3(...world), Matrix.Invert(parent.computeWorldMatrix(true))) : new Vector3(...world);
    object.transform.position.addInPlace(local);
    object.transform.computeWorldMatrix(true);
    object.captureTransform();
}

/** A position bound to a property snaps back at the next solve: it is named, not moved. */
const bound = (object: SceneObject): boolean => !!object.getBehavior(Properties)?.getData().transform?.position;

const along = (box: Box, k: 0 | 1, d: number): V3 => [box.axes[k][0] * d, box.axes[k][1] * d, box.axes[k][2] * d];

/** A line of the largest text in it (1.2 em), world metres; 0 when it holds none. */
function lineOf(object: SceneObject): number {
    let line = 0;
    for (const o of [object, ...object.getDescendants()]) {
        const size = o.getBehavior(Text)?._getResolvedData().fontSize;
        if (typeof size !== 'number') continue;
        const m = o.transform.computeWorldMatrix(true).m;
        line = Math.max(line, 1.2 * size / 100 * Math.hypot(m[4], m[5], m[6]));
    }
    return line;
}

/**
 * Keep what a build lays on its cards inside them. Of the objects the build created beside a card (its siblings),
 * those lying on the card's face (at least a third of them on it, off a side edge) are moved in it until they clear its edges by a margin; one
 * too big for that, or bound in place, is named. Text standing above the card's middle, less than a line of it clear
 * of its top (a caution over a parts card), moves the card and what lies on it down. Returns lines for the build result, '' when nothing moved.
 */
export function fitToCards(cards: readonly SceneObject[], created: ReadonlySet<SceneObject>): string {
    const lines: string[] = [];
    for (const card of cards) {
        const parent = card.getParent();
        let face = faceOf(card);
        if (!face || !parent) continue;
        const [w, h] = [face.half[0], face.half[1]];
        const margin = MARGIN * 2 * Math.min(w, h);
        const near = Math.min(w, h);
        const placed = parent.getChildren().filter(o => o !== card && created.has(o) && !cards.includes(o))
            .map(object => ({ object, box: drawnBox(object) }))
            .filter((x): x is { object: SceneObject; box: Box } => x.box !== null);
        const view = (box: Box) => seenFrom(face!, box);
        // On it: its middle on the face, or at least a third of it there, off a side edge (an icon at the edge).
        const on = placed.filter(x => { const v = view(x.box); return Math.abs(v.offset[0]) <= w + v.reach[0] / 3 && Math.abs(v.offset[1]) <= h && Math.abs(v.offset[2]) <= near; });
        // Text above it (its bottom above the card's middle): how far the lowest reaches into a line over its top edge.
        let drop = 0, over: SceneObject | null = null;
        for (const x of placed) {
            if (on.includes(x)) continue;
            const v = view(x.box);
            const line = lineOf(x.object);
            if (!line || v.offset[1] - v.reach[1] <= 0 || Math.abs(v.offset[0]) >= w + v.reach[0] || Math.abs(v.offset[2]) > near) continue;
            const need = h + Math.max(margin, line) - (v.offset[1] - v.reach[1]);
            if (need > drop) { drop = need; over = x.object; }
        }
        if (drop > 0 && over) {
            const moving = [card, ...on.map(x => x.object)];
            if (moving.some(bound)) lines.push(`Card "${card.name}" sits ${mm(drop)} too close under "${over.name}", but it or something on it is bound in place: move it down.`);
            else {
                for (const object of moving) shift(object, along(face, 1, -drop));
                lines.push(`Moved card "${card.name}" and what is on it down ${mm(drop)}, clear of "${over.name}".`);
                face = faceOf(card)!;
            }
        }
        let moved = 0, most = 0;
        for (const { object } of on) {
            const box = drawnBox(object)!;
            const v = view(box);
            const d = [0, 0];
            for (const k of [0, 1] as const) {
                const room = face.half[k] - margin;
                if (v.reach[k] > room) {
                    lines.push(`"${object.name}" is ${mm(2 * v.reach[k])} ${k ? 'tall' : 'wide'}; card "${card.name}" has room for ${mm(2 * room)}: shorten it, scale it down or enlarge the card.`);
                    continue;
                }
                d[k] = Math.max(-room - (v.offset[k] - v.reach[k]), Math.min(0, room - (v.offset[k] + v.reach[k])));
            }
            if (!d[0] && !d[1]) continue;
            if (bound(object)) { lines.push(`"${object.name}" crosses an edge of card "${card.name}" by ${mm(Math.hypot(d[0], d[1]))}, but its position is bound: move what is inside it.`); continue; }
            const a = along(face, 0, d[0]), b = along(face, 1, d[1]);
            shift(object, [a[0] + b[0], a[1] + b[1], a[2] + b[2]]);
            moved++;
            most = Math.max(most, Math.hypot(d[0], d[1]));
        }
        if (moved) lines.push(`Moved ${moved} object${moved === 1 ? '' : 's'} on card "${card.name}" inside its edges (up to ${mm(most)}).`);
    }
    return lines.length ? `\n\ncards: ${lines.join(' ')}` : '';
}
